import React from "react";
import { formatCurrency, toCapitalized } from "~/utils";
import { ProductAvailability } from "~/types/product";
import ColorSwatch from "../ColorSwatch";
import { SelectedVariant } from "~/types/variant";
import {
  Wrapper,
  Header,
  Heading,
  TotalLabel,
  TotalValue,
  Color,
  ColorLabel,
  ColorValue,
  Status,
  Table,
  LoadingState,
  TableHeader,
  TableHeaderText,
  TableCell,
} from "./components";

type Props = {
  isSignedIn?: boolean;
  selectedVariant?: SelectedVariant;
  productAvailability?: ProductAvailability;
  isLoading?: boolean;
};

const ProductBulkPricing: React.FC<Props> = ({
  isSignedIn,
  selectedVariant,
  productAvailability,
  isLoading = false,
}) => {
  if (!productAvailability || !selectedVariant) {
    return (
      <Wrapper isEmpty={true}>{isLoading ? <LoadingState /> : null}</Wrapper>
    );
  }

  const { fullProductTotal, selectedIndex, bulk, bulkRequiresSignIn } =
    productAvailability;
  const [headers, ...rows] = bulk;

  return (
    <Wrapper>
      {isLoading ? <LoadingState /> : null}
      <Header>
        <Heading>Bulk Pricing Guide</Heading>
        <TotalLabel>
          Current Product Total:&nbsp;{" "}
          <TotalValue>{formatCurrency(fullProductTotal)}</TotalValue>
        </TotalLabel>
        <Color>
          <ColorLabel>Color:</ColorLabel>
          <ColorSwatch
            hexCodes={selectedVariant.swatch.hexCodes}
            image={selectedVariant.swatch.url}
            size="24px"
            isActive={false}
            hasHover={false}
          />
          <ColorValue>{toCapitalized(selectedVariant.color)}</ColorValue>
        </Color>
        <Status>Status: {isSignedIn ? "Signed In" : "Guest"}</Status>
      </Header>
      <Table>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <TableHeader
                key={`header-${index}`}
                isSelected={index === selectedIndex}
                dataTestId={(() => {
                  if (header === "GUEST")
                    return "bulk-pricing__column-header--guest";
                  if (header === "MEMBER" || header === "$0+")
                    return "bulk-pricing__column-header--member";
                  return undefined;
                })()}
              >
                <TableHeaderText
                  isFirst={index === 0}
                  isSelected={index === selectedIndex}
                  isLessThanSelected={index < selectedIndex}
                  text={header}
                />
              </TableHeader>
            ))}
          </tr>
        </thead>
        {bulkRequiresSignIn && !isSignedIn ? (
          <tbody>
            <tr>
              <td
                data-testid="bulkPricingSignInMessage"
                colSpan={headers.length}
                className="pricing-guide__sign-in p-4 text-center text-sm font-semibold text-blue400"
              >
                <a
                  href="/users/sign_in"
                  className="underline visited:text-blue450"
                >
                  Sign in
                </a>
                &nbsp;to see bulk pricing for this product
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={`cell-${rowIndex}-${cellIndex}`}
                    isSignedIn={!!isSignedIn}
                    isLessThanSelected={
                      cellIndex > 0 && cellIndex < selectedIndex
                    }
                    isSelected={cellIndex === selectedIndex}
                  >
                    {typeof cell === "number" ? formatCurrency(cell) : cell}
                  </TableCell>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </Table>
    </Wrapper>
  );
};

export default ProductBulkPricing;
